<template>
  <div id="metadata-grid-editor">
    <title-panel
      :no-counter="true"
      :title="$t('metadataGridEditor.metadataEditor')"
    ></title-panel>

    <div class="buttons-panel">
      <vf-button
        :variant="buttonVariants.primary"
        @click="goToDocumentsOverview"
        >{{ $t('metadataGridEditor.cancel') }}</vf-button
      >
      <vf-button
        v-if="isMetadataInspectionMode()"
        :busy="isSavingChanges"
        :variant="buttonVariants.secondary"
        @click="() => handleSaveButton(false)"
        >{{ $t('metadataGridEditor.saveChanges') }}</vf-button
      >
      <vf-button
        :busy="isSavingChangesAndContinuing"
        :disabled="!isMetadataValid()"
        :variant="buttonVariants.secondary"
        @click="() => handleSaveButton(true)"
        >{{ $t('metadataGridEditor.saveChangesAndContinue') }}</vf-button
      >
    </div>

    <div v-if="isMetadataInspectionMode()" class="lower-button-panel">
      <vf-button
        :size="buttonSizes.small"
        :variant="buttonVariants.secondary"
        @click="() => moveCheckBotKksToMainKksField(true)"
        >{{ $t('metadataGridEditor.copyValidatedKks') }}
      </vf-button>
      <vf-button
        :size="buttonSizes.small"
        :variant="buttonVariants.quaternary"
        @click="() => moveCheckBotKksToMainKksField(false)"
        >{{ $t('metadataGridEditor.copyCheckBotKks') }}
      </vf-button>
      <vf-button
        v-if="kksBeforeAcceptance"
        :size="buttonSizes.small"
        :variant="buttonVariants.tertiary"
        @click="applyKksBeforeAcceptance"
        >{{ $t('metadataGridEditor.undoAcceptingKks') }}</vf-button
      >
    </div>

    <div id="metadata-table-container">
      <v-grid
        theme="compact"
        :apply-on-close="true"
        :col-size="150"
        :columns="columns"
        :column-types="plugin"
        :editors="gridEditors"
        :range="true"
        :resize="true"
        :row-definitions="rowDefinitions"
        :source="metadata"
      />
    </div>

    <div class="hints">
      <strong>{{ $t('metadataGridEditor.hint') }}:</strong>
      <div v-if="isQualityCheckMode()" class="change-icons-hint">
        <div class="not-changed-hint">
          {{ $t('metadataGridEditor.notChangedHint') }}
        </div>
        <div>
          {{ $t('metadataGridEditor.changedHint') }}
        </div>
      </div>

      <div v-if="isMetadataInspectionMode()" class="colors-hint">
        <div class="inference-hint doc-after-inference-hint">
          {{ $t('metadataGridEditor.docAfterInferenceHint') }}
        </div>
        <div class="inference-hint inferred-value-hint">
          {{ $t('metadataGridEditor.inferredValueHint') }}
        </div>
      </div>
    </div>
    <modal-wrapper
      v-model:is-visible="dccDialogVisible"
      :header="$t('dccProposalsCell.applyTo')"
      :display-footer="false"
    >
      <div class="dcc-field-picker">
        <p class="dcc-tag-preview">{{ dccPendingTag?.label }}</p>
        <div class="dcc-field-buttons">
          <vf-button
            :variant="buttonVariants.secondary"
            @click="applyDcc('DocumentClass')"
          >
            {{ $t('dccProposalsCell.masterDcc') }}
          </vf-button>
          <vf-button
            :variant="buttonVariants.secondary"
            @click="applyDcc('DocumentClassContent')"
          >
            {{ $t('dccProposalsCell.contentDcc') }}
          </vf-button>
        </div>
      </div>
    </modal-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import endpointsList from '@/helpers/endpointsList';
  import { METADATA_GRID_DOCUMENT_IDS_LOCAL_STORAGE_KEY } from '@/interfaces/constants';
  import {
    PROJECT_ID_PARAM,
    PROJECT_TYPE_PARAM,
    Route,
    routeParams,
  } from '@/routes';
  import { ProjectDataService } from '@/services';
  import SelectTypePlugin from '@revolist/revogrid-column-select';
  import VGrid, {
    ColumnProp,
    ColumnRegular,
    Editors,
    VGridVueEditor,
    VGridVueTemplate,
  } from '@revolist/vue3-datagrid';
  import axios, { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
  import { cloneDeep, debounce, isEqual, isNil } from 'lodash';
  import {
    CHECK_BOT_ESTABLISHED_KKS,
    CheckBotInferenceStatus,
    ContentTypeFieldType,
    DEFAULT_METADATA_VALUES_SEPARATOR,
    DOCUMENT_ID,
    ICheckBotDataForFiles,
    IDocumentMetadata,
    IDocumentMetadataWithFieldMetadataDto,
    IGetCheckBotEstablishedKksForDocumentsDto,
    IGetInferenceStatusForDocumentsDto,
    IInferenceSatusesForFiles,
    IMetadataAndFieldMetadata,
    IUpdateMetadataOfMultipleDocumentsDto,
    IUpdateMultipleQualityCheckStatusesDto,
    KKS,
    ORIGNINAL_FILE_NAME_METADATA_FIELD,
    ProjectType,
    QualityCheckStatus,
    Serialized,
    UniversalMappingField,
  } from 'types';
  import {
    deduplicateArray,
    divideArray,
    isDefined,
    normalizeTermName,
  } from 'utils';
  import { DigitalizationProjectDetailsTab } from '../DigitalizationProjectDetails/DigitalizationProjectDetails.types';
  import { IconForData } from './customCells/CellWithIcon/CellWithIcon.script';
  import CellWithIcon from './customCells/CellWithIcon/CellWithIcon.vue';
  import CheckBotKksCell from './customCells/CheckBotKksCell/CheckBotKksCell.vue';
  import DccProposalsCell from './customCells/DccProposalsCell/DccProposalsCell.vue';
  import DateEditor from './customEditors/DateEditor/DateEditor.vue';
  import TagEditor from './customEditors/TagEditor/TagEditor.vue';
  import { useKksValidator } from '@/composables/useKksValidator';
  import { useToast } from '@/composables/useToast';
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import { watch } from 'vue';
  import { onMounted } from 'vue';
  import {
    ColumnTypeAndEditor,
    MetadataGridEditorMode,
    ColumnPin,
    ColumnType,
    RowDefinition,
    KksBeforeAcceptance,
    DocumentObjectIdsAndUploadIds,
    DccTag,
  } from './MetadataGridEditor.types';
  import { createVNode } from 'vue';
  import KksEditor from './customEditors/KksEditor/KksEditor.vue';
  import ModalWrapper from '@/components/ModalWrapper/ModalWrapper.vue';

  const getMarkedCellStyle = (
    borderColor: '#5c1413' | 'red' | '#36286d',
    pointerOnHover = false,
  ) => ({
    display: 'block',
    borderWidth: '.08rem',
    borderStyle: 'solid',
    borderColor,
    cursor: pointerOnHover ? 'pointer' : 'auto',
  });
  const cellStyles = {
    docId: { cursor: 'pointer' },
    idOfDocAfterInference: getMarkedCellStyle('#36286d', true),
    checkBotInferredValue: getMarkedCellStyle('#5c1413'),
    missingRequired: getMarkedCellStyle('red'),
    invalidValue: getMarkedCellStyle('red'),
  };

  const { validateKks, kksValidationList, isValidatingKks } = useKksValidator();
  const router = useRouter();
  const { createErrorToast, createSuccessToast, createWarningToast } =
    useToast();
  const { t } = useI18n();

  const props = defineProps<{
    digitalizationProjectId: string;
    mode: MetadataGridEditorMode;
  }>();

  const changedMetadata = ref<Record<string, boolean>>({});
  const changeIndicatorIcons = ref<IconForData[]>([]);
  const columns = ref<ColumnRegular[]>([]);
  const documentObjectIds = ref<string[]>();
  const documentObjectIdsAndUploadIds = ref<DocumentObjectIdsAndUploadIds[]>();
  const documentsMetadataWithFieldInfo =
    ref<IDocumentMetadataWithFieldMetadataDto[]>();
  const projectLocation = ref<string | undefined>(undefined);
  const gridEditors = ref<Editors>({
    datePicker: VGridVueEditor(DateEditor),
    kksEditor: VGridVueEditor(
      createVNode(KksEditor, { projectLocation: projectLocation }),
    ),
    tagEditor: VGridVueEditor(
      createVNode(TagEditor, { fieldInfo: documentsMetadataWithFieldInfo }),
    ),
  });
  const inferenceStatuses = ref<IInferenceSatusesForFiles>({});
  const initialMetadata = ref<IDocumentMetadata[]>([]);
  const isGettingMetadata = ref(false);
  const isSavingChanges = ref(false);
  const isSavingChangesAndContinuing = ref(false);
  const kksBeforeAcceptance = ref<KksBeforeAcceptance>();
  const metadata = ref<IDocumentMetadata[]>();
  const plugin = ref({ select: new SelectTypePlugin() });
  const dccDialogVisible = ref(false);
  const dccPendingTag = ref<DccTag | null>(null);
  const dccPendingDocumentId = ref<string | null>(null);
  const projectDataService = ref(
    new ProjectDataService(ProjectType.DIGITALIZATION_PROJECT),
  );
  const rowDefinitions = ref<RowDefinition[]>([]);

  function getDocumentObjectIdsFromLocalStorage() {
    const stringifiedIds = localStorage.getItem(
      METADATA_GRID_DOCUMENT_IDS_LOCAL_STORAGE_KEY,
    );
    if (stringifiedIds === null) return;
    const ids: DocumentObjectIdsAndUploadIds[] = JSON.parse(stringifiedIds);

    documentObjectIds.value = ids.map((id) => id.documentObjectId);
    documentObjectIdsAndUploadIds.value = ids;
  }

  async function getMetadata() {
    try {
      isGettingMetadata.value = true;

      const response = await axios.post<
        IDocumentMetadataWithFieldMetadataDto[]
      >(
        endpointsList.digitalizationUploads.getDocumentMetadataByIds(
          props.digitalizationProjectId,
          false,
          [ORIGNINAL_FILE_NAME_METADATA_FIELD as string],
        ),
        documentObjectIds.value,
      );

      documentsMetadataWithFieldInfo.value = response.data;
      const meta = transformMetadaResponseToArrayOfObjects(response.data);

      metadata.value = meta;
      initialMetadata.value = cloneDeep(meta);

      await appendCheckBotDataToMetadata();

      transformMetadataToColumns();
    } catch {
      createErrorToast(t('metadataGridEditor.errors.errorGettingMetadata'));
    } finally {
      isGettingMetadata.value = false;
    }
  }

  function transformMetadaResponseToArrayOfObjects(
    response: IDocumentMetadataWithFieldMetadataDto[],
  ): IDocumentMetadata[] {
    return response.map((metadataAndId) =>
      Object.fromEntries(
        metadataAndId.metadata.map((metadata) => [
          metadata.fieldName,
          metadata.fieldValue,
        ]),
      ),
    ) as IDocumentMetadata[];
  }

  function transformMetadataToColumns() {
    if (!documentsMetadataWithFieldInfo.value) return;

    const cols = Object.keys(metadata.value?.[0] ?? {})
      .filter((key) => getFieldInfoByFieldName(key))
      .map((key) => {
        const fieldInfo = getFieldInfoByFieldName(key);
        if (!fieldInfo) return;

        const columnSettings = getColumnTypeByDataType(
          fieldInfo.fieldType,
          key,
        );
        const source = fieldInfo.possibleValues?.map((value) => ({
          label: value,
          value,
        }));
        return {
          prop: key,
          name: t(
            `contentTypeFields.${fieldInfo.fieldDisplayName}`,
          )?.toString(),
          source,
          cellProperties: ({ prop, model }: any) => {
            return {
              style: getCellStyle(model[prop], fieldInfo),
              onClick: () => {
                handleCellClick(prop, model[prop]);
              },
            };
          },
          readonly: fieldInfo.readonly,
          pin:
            fieldInfo.fieldName === UniversalMappingField.DOCUMENT_ID
              ? ColumnPin.COL_PIN_START
              : undefined,
          ...columnSettings,
          size: fieldInfo.fieldName === KKS ? 400 : undefined,
        };
      })
      .filter(isDefined);

    columns.value = cols;
    appendCheckBotEstablishedKksColumn();
    sortColumns();
  }

  function sortColumns() {
    const order: ColumnProp[] = [
      'OriginalFilename',
      /**
       * Temporary fix to deal with typo in `OriginalFileName` field in
       * Queportdev SharePoint.
       */
      'OriginalFIlename',
      'CreatorDocumentNumber',
      /**
       * Temporary fix to deal with typo in `CreatorDocumentNumber` field in
       * Vattenfall SharePoint.
       */
      'CreaterDocumentNumber',
      'Title',
      KKS as string,
      CHECK_BOT_ESTABLISHED_KKS as string,
      'DocumentClass',
      'DocumentClassContent',
      'dccProposals',
      'DocLanguage',
      'Revision',
      'DocumentCreator',
      'CreateDate',
      'ReleaseDate',
    ];
    columns.value = columns.value.sort((a, b) => {
      const indexA = order.indexOf(a.prop);
      const indexB = order.indexOf(b.prop);

      /**
       * If the key is not in the predefinedOrder array, place it after the
       * sorted ones.
       */
      if (indexA === -1 && indexB !== -1) return 1;
      if (indexA !== -1 && indexB === -1) return -1;

      return indexA - indexB;
    });
  }

  function getColumnTypeByDataType(
    dataType: ContentTypeFieldType,
    fieldName: string,
  ): ColumnTypeAndEditor {
    const columnException = getColumnException(fieldName);
    if (columnException) return columnException;

    switch (dataType) {
      case ContentTypeFieldType.TEXT:
        return { columnType: ColumnType.STRING };
      case ContentTypeFieldType.NOTE:
        return { columnType: ColumnType.STRING };
      case ContentTypeFieldType.DATE_TIME:
        return {
          columnType: ColumnType.DATE,
          editor: gridEditors.value.datePicker,
        };
      case ContentTypeFieldType.TAXONOMY_FIELD_TYPE:
        return {
          columnType: ColumnType.SELECT,
          valueKey: 'value',
          labelKey: 'label',
        };
      case ContentTypeFieldType.TAXONOMY_FIELD_TYPE_MULTI:
        return { editor: gridEditors.value.tagEditor };
      default:
        return { columnType: ColumnType.STRING };
    }
  }

  function getColumnException(
    fieldName: string,
  ): ColumnTypeAndEditor | undefined {
    if (fieldName === UniversalMappingField.DOCUMENT_ID) {
      return {
        cellTemplate: VGridVueTemplate(CellWithIcon, {
          icons: ['🔗'],
          buildIconsForDataFunction:
            props.mode === MetadataGridEditorMode.QUALITY_CHECK
              ? buildChangeIndicatorIcons
              : undefined,
        }),
      };
    }
    if (fieldName === KKS) {
      return {
        editor: gridEditors.value.kksEditor,
      };
    }
  }

  function isMetadataValid(): boolean {
    return (
      !!metadata.value &&
      metadata.value.every((doc) =>
        Object.entries(doc).every(([fieldName, fieldValue]) => {
          const fieldInfo = getFieldInfoByFieldName(fieldName);
          if (!fieldInfo) return true;
          return (
            !isEmptyRequired(fieldValue, fieldInfo) &&
            isValidIfTaxonomyValue(fieldValue, fieldInfo)
          );
        }),
      )
    );
  }

  function getCellStyle(
    currentValue: string,
    fieldInfo: IMetadataAndFieldMetadata,
  ) {
    /** Empty required value */
    if (isEmptyRequired(currentValue, fieldInfo)) {
      return cellStyles.missingRequired;
    } else if (!isValidIfTaxonomyValue(currentValue, fieldInfo)) {
      /** Invalid taxonomy value */
      return cellStyles.invalidValue;
    } else if (
      /** Check bot inferred value */
      currentValue !== undefined &&
      fieldInfo.checkBotInferredValue !== undefined &&
      currentValue === fieldInfo.checkBotInferredValue
    ) {
      return cellStyles.checkBotInferredValue;
    } else if (
      currentValue !== undefined &&
      fieldInfo.fieldName === UniversalMappingField.DOCUMENT_ID
    ) {
      if (isMetadataInferedForDocumentId(currentValue)) {
        return cellStyles.idOfDocAfterInference;
      } else {
        return cellStyles.docId;
      }
    } else {
      return {};
    }
  }

  function getFieldInfoByFieldName(
    fieldName: string,
  ): IMetadataAndFieldMetadata | undefined {
    return documentsMetadataWithFieldInfo.value?.[0].metadata.find(
      (field) => field.fieldName === fieldName,
    );
  }

  function isValueEmpty(value: string | number): boolean {
    return isNil(value) || value === '';
  }

  function isEmptyRequired(
    value: string | number,
    fieldInfo: IMetadataAndFieldMetadata,
  ): boolean {
    return fieldInfo.required && isValueEmpty(value);
  }

  function isValidIfTaxonomyValue(
    value: string,
    fieldInfo: IMetadataAndFieldMetadata,
  ): boolean {
    const isTaxonomyValue = [
      ContentTypeFieldType.TAXONOMY_FIELD_TYPE,
      ContentTypeFieldType.TAXONOMY_FIELD_TYPE_MULTI,
    ].includes(fieldInfo.fieldType);
    if (!isTaxonomyValue) return true;
    if (isValueEmpty(value)) return true;

    const values = value
      .split(DEFAULT_METADATA_VALUES_SEPARATOR)
      .map((v) => normalizeTermName(v));
    const possibleValues = fieldInfo.possibleValues?.map((v) =>
      normalizeTermName(v),
    );
    return values.every((val) => possibleValues?.includes(val));
  }

  function goToDocumentsOverview() {
    router.push({
      name: Route.DIGITALIZATION_PROJECT_DETAILS,
      params: {
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].projectId]:
          props.digitalizationProjectId.toString(),
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].tab]:
          DigitalizationProjectDetailsTab.DOCUMENTS,
      },
    });
  }

  function buildMetadataUpdateDto(
    metadata: IDocumentMetadata[],
  ): IUpdateMetadataOfMultipleDocumentsDto {
    if (!documentsMetadataWithFieldInfo.value) return {};

    return Object.fromEntries(
      metadata.map((metadata) => {
        const _id = documentsMetadataWithFieldInfo.value!.find(
          (document) =>
            document.metadata.find((field) => field.fieldName === 'documentId')
              ?.fieldValue === metadata.documentId,
        )?._id;

        return [_id, metadata];
      }),
    );
  }

  async function handleMetadataSave(
    acceptMetadataInspection: boolean,
    metadata: IDocumentMetadata[],
  ): Promise<void> {
    const dto = buildMetadataUpdateDto(metadata);

    try {
      await axios.patch(
        endpointsList.digitalizationUploads.updateMetadataOfMultipleDocuments(
          props.digitalizationProjectId,
          acceptMetadataInspection,
        ),
        dto,
      );

      createSuccessToast(t('metadataGridEditor.metadataSaved'));
    } catch (err: unknown) {
      if (
        err instanceof AxiosError &&
        err.response?.status === HttpStatusCode.BadRequest
      ) {
        createErrorToast(
          t('metadataGridEditor.errors.errorValidatingMetadata'),
        );
      } else {
        createErrorToast(
          t('metadataGridEditor.errors.errorSavingMetadataChanges'),
        );
      }

      throw err;
    }
  }

  function buildQualityCheckUpdateDto(
    documentObjectIds: string[],
    qualityCheckStatus: QualityCheckStatus,
  ): Serialized<IUpdateMultipleQualityCheckStatusesDto> {
    return {
      digitalizationProjectId: props.digitalizationProjectId,
      documentObjectIds,
      qualityCheckStatus,
    };
  }

  async function handleAcceptQualityCheck(
    documentObjectIds: string[],
  ): Promise<void> {
    try {
      await axios.patch(
        endpointsList.digitalizationUploads
          .setQualityCheckStatusesOfMultipleDocuments,
        buildQualityCheckUpdateDto(
          documentObjectIds,
          QualityCheckStatus.ACCEPTED,
        ),
      );
    } catch (err) {
      createErrorToast(
        t('metadataGridEditor.errors.errorUpdatingQualityCheck'),
      );

      throw err;
    }
  }

  async function handleSaveButton(
    acceptMetadataInspection: boolean,
  ): Promise<void> {
    /**
     * HACK: Timeout used to give RevoGrid time to save the currently edited
     * value if the button was clicked during cell editing.
     */
    await new Promise((resolve) => {
      setTimeout(resolve, 200);
    });
    try {
      if (!metadata.value) return;

      setSaveButtonBusyState(acceptMetadataInspection, true);

      if (isMetadataInspectionMode()) {
        await handleMetadataSave(acceptMetadataInspection, metadata.value);
      } else {
        const [changed, notChanged] = divideArray(
          metadata.value,
          (doc) => changedMetadata.value[doc.documentId],
        );
        const docObjectIdsOfNotChanged = notChanged.map((doc) =>
          getDocumentObjectIdByDocumentId(doc.documentId),
        );

        const updatePromises: Promise<void>[] = [];
        if (changed.length) {
          updatePromises.push(handleMetadataSave(true, changed));
        }
        if (notChanged.length) {
          updatePromises.push(
            handleAcceptQualityCheck(docObjectIdsOfNotChanged),
          );
        }

        await Promise.all(updatePromises);
      }

      goToDocumentsOverview();
    } finally {
      setSaveButtonBusyState(acceptMetadataInspection, false);
    }
  }

  function setSaveButtonBusyState(
    acceptMetadataInspection: boolean,
    isBusy: boolean,
  ): void {
    if (acceptMetadataInspection) {
      isSavingChangesAndContinuing.value = isBusy;
    } else {
      isSavingChanges.value = isBusy;
    }
  }

  async function getCheckBotDataForDocuments() {
    if (!documentObjectIds.value) return;

    const body: Serialized<IGetCheckBotEstablishedKksForDocumentsDto> = {
      documentObjectIds: documentObjectIds.value,
    };

    const response: AxiosResponse<ICheckBotDataForFiles> = await axios.post(
      endpointsList.digitalizationUploads.getCheckBotDataForDocuments,
      body,
    );

    return response.data;
  }

  async function appendCheckBotDataToMetadata(): Promise<void> {
    const checkBotData = await getCheckBotDataForDocuments();

    if (!metadata.value || !checkBotData) return;

    metadata.value = metadata.value.map((doc) => {
      const docuObjectId = documentsMetadataWithFieldInfo.value?.find(
        (metadataForDoc) =>
          metadataForDoc.metadata.find(
            (field) => field.fieldName === DOCUMENT_ID,
          )?.fieldValue === doc.documentId,
      )?._id;
      if (!docuObjectId) return doc;

      const data = checkBotData[docuObjectId.toString()];
      if (!data) return doc;

      const deduplicatedKks = deduplicateArray([
        ...(data.checkBotEstablishedKKS ?? []),
        // ...(data.checkBotEstablishedAKS ?? []),
      ]);
      const filteredKks = deduplicatedKks.filter(
        (kks) =>
          !doc.KKS?.split(DEFAULT_METADATA_VALUES_SEPARATOR).includes(kks),
      );

      doc.checkBotEstablishedKKS = filteredKks;
      doc.dccProposals = data.dccProposals;
      doc.dccAiPrediction = data.dccAiPrediction;

      return doc;
    });
  }

  function appendCheckBotEstablishedKksColumn(): void {
    columns.value.push({
      columnType: ColumnType.STRING,
      prop: CHECK_BOT_ESTABLISHED_KKS as string,
      name: t('metadataGridEditor.checkBotEstablishedKksNumbers').toString(),
      readonly: true,
      size: 300,
      cellTemplate: VGridVueTemplate(CheckBotKksCell, {
        calculateRowHeightFunction: calculateCheckBotEstablishedKksCellHeight,
        documentObjectIdsByDocumentIds: getDocumentObjectIdsForDocumentIds(),
        updateMetadataFunction: handleCheckBotKksAccepted,
        kksValidationList,
        isValidatingKks,
      }),
    });

    columns.value.push({
      columnType: ColumnType.STRING,
      prop: 'dccProposals',
      name: t('metadataGridEditor.dccProposals').toString(),
      readonly: true,
      size: 300,
      cellTemplate: VGridVueTemplate(DccProposalsCell, {
        calculateRowHeightFunction: calculateCheckBotEstablishedKksCellHeight,
        updateMetadataFunction: handleDccAccepted,
        labelAi: t('dccProposalsCell.aiLabel'),
      }),
    });
  }

  function handleDccAccepted(tag: DccTag, documentId: string) {
    dccPendingTag.value = tag;
    dccPendingDocumentId.value = documentId;
    dccDialogVisible.value = true;
  }

  function findMatchingPossibleValue(
    fieldName: string,
    dccValue: string,
  ): string | undefined {
    const possibleValues = getFieldInfoByFieldName(fieldName)?.possibleValues;
    return possibleValues?.find(
      (v) => normalizeTermName(v) === normalizeTermName(dccValue),
    );
  }

  function applyDcc(field: 'DocumentClass' | 'DocumentClassContent') {
    const tag = dccPendingTag.value;
    const documentId = dccPendingDocumentId.value;
    if (!tag || !documentId) return;

    const meta = metadata.value?.find((doc) => doc.documentId === documentId);
    if (!meta) return;

    const matchedValue = findMatchingPossibleValue(field, tag.value);
    if (!matchedValue) {
      createErrorToast(t('dccProposalsCell.noMatchingValue'));
      dccDialogVisible.value = false;
      dccPendingTag.value = null;
      dccPendingDocumentId.value = null;
      return;
    }

    if (field === 'DocumentClass') {
      if (meta.DocumentClass === matchedValue) {
        createWarningToast(
          t('dccProposalsCell.alreadyExists', {
            field: t('dccProposalsCell.masterDcc'),
          }),
        );
        dccDialogVisible.value = false;
        dccPendingTag.value = null;
        dccPendingDocumentId.value = null;
        return;
      }
      meta.DocumentClass = matchedValue;
    } else {
      const existing =
        meta.DocumentClassContent?.split(DEFAULT_METADATA_VALUES_SEPARATOR) ??
        [];
      if (existing.includes(matchedValue)) {
        createWarningToast(
          t('dccProposalsCell.alreadyExists', {
            field: t('dccProposalsCell.contentDcc'),
          }),
        );
        dccDialogVisible.value = false;
        dccPendingTag.value = null;
        dccPendingDocumentId.value = null;
        return;
      }
      meta.DocumentClassContent = meta.DocumentClassContent
        ? `${meta.DocumentClassContent}${DEFAULT_METADATA_VALUES_SEPARATOR}${matchedValue}`
        : matchedValue;
    }

    meta.dccProposals = meta.dccProposals?.filter((v) => v !== tag.value);
    if (meta.dccAiPrediction?.dcc === tag.value) {
      meta.dccAiPrediction = undefined;
    }

    metadata.value = cloneDeep(metadata.value);
    dccDialogVisible.value = false;
    dccPendingTag.value = null;
    dccPendingDocumentId.value = null;
  }

  function handleCheckBotKksAccepted(kks: string, documentId: string) {
    const meta = metadata.value?.find((doc) => doc.documentId === documentId);
    if (!meta) throw new Error();

    if (!meta.KKS) {
      meta.KKS = kks;
    } else {
      meta.KKS = `${meta.KKS}${DEFAULT_METADATA_VALUES_SEPARATOR}${kks}`;
    }

    meta!.checkBotEstablishedKKS = meta.checkBotEstablishedKKS!.filter(
      (checkBotKks) => checkBotKks !== kks,
    );

    metadata.value = cloneDeep(metadata.value);
  }

  const calculateCheckBotEstablishedKksCellHeight = debounce(() => {
    const checkBotKksNodes = document.querySelectorAll('.check-bot-kks-cell');
    checkBotKksNodes.forEach((node, index) => {
      const size = (node as HTMLElement).offsetHeight;

      rowDefinitions.value = [
        ...rowDefinitions.value,
        {
          size,
          type: 'rgRow',
          index,
        },
      ];
    });
  });

  function getDocumentObjectIdsForDocumentIds(): Record<string, string> {
    return (
      documentsMetadataWithFieldInfo.value?.reduce(
        (ids, doc) => {
          const documentId = doc.metadata.find(
            (field) => field.fieldName === DOCUMENT_ID,
          )?.fieldValue;
          if (!documentId) throw new Error();

          ids[documentId] = doc._id.toString();
          return ids;
        },
        {} as Record<string, string>,
      ) ?? {}
    );
  }

  function saveKksBeforeAcceptance() {
    kksBeforeAcceptance.value =
      metadata.value?.reduce((kksState, doc) => {
        kksState[doc.documentId] = {
          KKS: doc.KKS,
          checkBotEstablishedKKS: doc.checkBotEstablishedKKS,
        };

        return kksState;
      }, {} as KksBeforeAcceptance) ?? {};
  }

  function applyKksBeforeAcceptance() {
    if (!kksBeforeAcceptance.value) throw new Error();

    Object.entries(kksBeforeAcceptance.value).forEach(([docId, kksState]) => {
      const meta = metadata.value?.find((doc) => doc.documentId === docId);
      if (!meta) throw Error();

      meta.KKS = kksState.KKS;
      meta.checkBotEstablishedKKS = kksState.checkBotEstablishedKKS;
    });
  }

  async function moveCheckBotKksToMainKksField(
    onlyValidated: boolean,
  ): Promise<void> {
    saveKksBeforeAcceptance();

    metadata.value?.forEach((doc) => {
      doc.checkBotEstablishedKKS?.forEach((kks) => {
        if (
          !onlyValidated ||
          kksValidationList.value.some(
            (kksItem) => kksItem.kks === kks && kksItem.exists,
          )
        ) {
          handleCheckBotKksAccepted(kks, doc.documentId);
        }
      });
    });
  }

  function getAllKks(): string[] {
    const checkBotKks =
      metadata.value
        ?.flatMap((doc) => doc.checkBotEstablishedKKS)
        .filter(isDefined) ?? [];
    const kks =
      metadata.value
        ?.flatMap((doc) => doc.KKS?.split(DEFAULT_METADATA_VALUES_SEPARATOR))
        .filter(isDefined) ?? [];

    return [...checkBotKks, ...kks];
  }

  async function validateAllKks(): Promise<void> {
    if (!projectLocation.value) return;
    const kks = getAllKks();
    await validateKks(kks, projectLocation.value);
  }

  async function getCheckBotInferenceStatuses(): Promise<void> {
    if (!documentObjectIds.value) return;

    const body: Serialized<IGetInferenceStatusForDocumentsDto> = {
      documentObjectIds: documentObjectIds.value,
    };
    try {
      const response = await axios.post(
        endpointsList.digitalizationUploads
          .getCheckBotInferenceStatusForMultipleDocuments,
        body,
      );

      inferenceStatuses.value = response.data;
    } catch {
      createErrorToast(
        t('metadataGridEditor.errors.errorGettingInferenceStatuses'),
      );
    }
  }

  function getDocumentByDocumentId(
    documentId: string,
  ): IDocumentMetadataWithFieldMetadataDto | undefined {
    return documentsMetadataWithFieldInfo.value?.find((doc) =>
      doc.metadata.some(
        (metadata) =>
          metadata.fieldName === UniversalMappingField.DOCUMENT_ID &&
          metadata.fieldValue === documentId,
      ),
    );
  }

  function getDocumentObjectIdByDocumentId(documentId: string): string {
    const id = getDocumentByDocumentId(documentId)?._id;
    if (!id) throw new Error();

    return id.toString();
  }

  function isMetadataInferedForDocumentId(documentId: string): boolean {
    const docObjectId = getDocumentObjectIdByDocumentId(documentId);
    return (
      inferenceStatuses.value[docObjectId] ===
      CheckBotInferenceStatus.DATA_RECEIVED
    );
  }

  function handleCellClick(field: string, value: string): void {
    if (field !== UniversalMappingField.DOCUMENT_ID) return;
    const documentObjectId = getDocumentObjectIdByDocumentId(value);
    goToDocumentDetailView(documentObjectId);
  }

  function goToDocumentDetailView(documentOid: string): void {
    const routeData = router.resolve({
      name: Route.DOCUMENT_DETAIL_VIEW,
      params: {
        [routeParams[Route.DOCUMENT_DETAIL_VIEW][PROJECT_TYPE_PARAM]]:
          ProjectType.DIGITALIZATION_PROJECT,
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].documentOid]: documentOid,
      },
      query: {
        [routeParams[Route.DOCUMENT_DETAIL_VIEW].query[PROJECT_ID_PARAM]]:
          props.digitalizationProjectId,
      },
    });

    window.open(routeData.href, '_blank');
  }

  function getChangedMetadata(): void {
    metadata.value?.forEach((doc) => {
      const initialMetadataDoc = initialMetadata.value?.find(
        (initialDoc) => initialDoc.documentId === doc.documentId,
      );

      const {
        checkBotEstablishedKKS,
        dccProposals,
        dccAiPrediction,
        ...docWithoutCheckBotKks
      } = doc;

      changedMetadata.value[doc.documentId] = !isEqual(
        JSON.parse(JSON.stringify(docWithoutCheckBotKks)),
        JSON.parse(JSON.stringify(initialMetadataDoc)),
      );
    });
  }

  function buildChangeIndicatorIcons(): IconForData[] {
    return (changeIndicatorIcons.value = Object.entries(
      changedMetadata.value,
    ).map(([docId, isChanged]) => ({
      data: docId,
      icon: isChanged ? '✘' : '✔',
    })));
  }

  function isMetadataInspectionMode(): boolean {
    return props.mode === MetadataGridEditorMode.METADATA_INSPECTION;
  }

  function isQualityCheckMode(): boolean {
    return props.mode === MetadataGridEditorMode.QUALITY_CHECK;
  }

  async function getProjectLocation(): Promise<void> {
    projectLocation.value = await projectDataService.value.getProjectLocation(
      props.digitalizationProjectId,
    );
  }

  watch(
    () => metadata.value,
    () => {
      validateAllKks();
      getChangedMetadata();
      calculateCheckBotEstablishedKksCellHeight();
      getProjectLocation();
    },
    { deep: true },
  );
  watch(
    () => projectLocation.value,
    () => {
      validateAllKks();
    },
  );

  onMounted(() => {
    getDocumentObjectIdsFromLocalStorage();
    getMetadata();
    getCheckBotInferenceStatuses();
    validateAllKks();
  });
</script>

<style lang="scss" scoped>
  :deep(revo-grid) .main-viewport {
    height: auto;
  }

  .dcc-field-picker {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .dcc-tag-preview {
      font-weight: bold;
      margin: 0;
    }

    .dcc-field-buttons {
      display: flex;
      justify-content: space-between;
      gap: 0.75rem;
    }
  }

  #metadata-grid-editor {
    .buttons-panel {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      column-gap: 2rem;
      margin-bottom: 2rem;
    }

    .lower-button-panel {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      column-gap: 2rem;
      margin-bottom: 2rem;
    }

    .hints {
      margin-top: 2rem;

      .colors-hint {
        .doc-after-inference-hint {
          /** Must be same as in components script */
          color: #36286d;
        }

        .inferred-value-hint {
          color: #5c1413;
        }
      }
    }
  }

  :deep(.horizontal) {
    z-index: 0;
  }
</style>
